import {
  ACCEPT_IMAGES,
  ACCEPT_VIDEO,
  ONE_MB,
  sendErrorMessages,
} from '../constants';

type ValidateFileArgs = {
  file: File;
  acceptedTypes: string[];
  maxBytes: number;
  toastError: (err: Error) => void;
  // サイズ超過時の文言（省略時は従来どおり写真用）。'$size' が上限に置換される
  sizeErrorMessage?: string;
};

const MIME_LABEL_MAP: Record<string, string> = {
  jpeg: 'JPEG',
  'svg+xml': 'SVG',
  quicktime: 'QUICKTIME',
};

export const isImageFile = (file: File) =>
  ACCEPT_IMAGES.includes(file.type) || file.type.startsWith('image/');

export const isVideoFile = (file: File) =>
  ACCEPT_VIDEO.includes(file.type) || file.type.startsWith('video/');

export const formatAcceptedTypes = (mimeTypes: string[]): string => {
  const labels = new Set<string>();

  for (const mime of mimeTypes) {
    const [, subtype] = mime.split('/');
    if (!subtype) continue;

    labels.add(MIME_LABEL_MAP[subtype] ?? subtype.toUpperCase());
  }

  return Array.from(labels).join(', ');
};

const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

const startsWithBytes = (bytes: Uint8Array, signature: number[]): boolean =>
  signature.every((value, index) => bytes[index] === value);

// MIMEタイプは自己申告なので、LINEへ送る画像はマジックバイトで実体を検証する
export const hasJpegOrPngSignature = async (file: File): Promise<boolean> => {
  const bytes = new Uint8Array(
    await file.slice(0, PNG_SIGNATURE.length).arrayBuffer(),
  );
  return (
    startsWithBytes(bytes, JPEG_SIGNATURE) ||
    startsWithBytes(bytes, PNG_SIGNATURE)
  );
};

export const validateFile = ({
  file,
  acceptedTypes,
  maxBytes,
  toastError,
  sizeErrorMessage,
}: ValidateFileArgs): boolean => {
  if (!acceptedTypes.includes(file.type)) {
    const accepted = formatAcceptedTypes(acceptedTypes);
    toastError(
      new Error(
        sendErrorMessages.invalidFileFormat.replace('$formats', accepted),
      ),
    );
    return false;
  }

  if (file.size > maxBytes) {
    toastError(
      new Error(
        (sizeErrorMessage ?? sendErrorMessages.limitSize).replace(
          '$size',
          `${maxBytes / ONE_MB}MB`,
        ),
      ),
    );
    return false;
  }

  return true;
};
